import { Link } from "react-router-dom";
import { LOGO_URL, CONTACT } from "@/lib/constants";
import { Github, Twitter, Rss, Phone, Mail } from "lucide-react";

export default function Footer() {
    return (
        <footer data-testid="site-footer" className="border-t border-zinc-800 mt-24 bg-zinc-950/60">
            <div className="container-tight py-16 grid grid-cols-1 md:grid-cols-12 gap-10">
                <div className="md:col-span-4">
                    <div className="flex items-center gap-3">
                        <div className="h-10 w-10 rounded-sm overflow-hidden bg-white p-0.5">
                            <img src={LOGO_URL} alt="All About Printers" className="h-full w-full object-contain" />
                        </div>
                        <div>
                            <div className="font-display font-bold text-white">All About Printers</div>
                            <div className="text-xs text-zinc-500 tracking-[0.2em] uppercase mt-1">Est. 2025</div>
                        </div>
                    </div>
                    <p className="text-zinc-400 text-sm mt-5 max-w-md leading-relaxed">
                        Independent editorial reviews grounded in manufacturer documentation, published research,
                        and community feedback. For informational and educational purposes only.
                    </p>

                    <div className="mt-6 space-y-3 text-sm">
                        <a href={CONTACT.phoneHref} data-testid="footer-phone" className="flex items-center gap-3 text-zinc-200 hover:text-blue-400 transition-colors group">
                            <span className="h-9 w-9 rounded-sm bg-blue-600/15 border border-blue-600/30 flex items-center justify-center">
                                <Phone className="h-4 w-4 text-blue-400" />
                            </span>
                            <span>
                                <span className="block font-display text-base font-semibold">{CONTACT.phoneDisplay}</span>
                                <span className="block text-[11px] tracking-[0.2em] uppercase text-zinc-500 mt-0.5">Toll free · {CONTACT.hours}</span>
                            </span>
                        </a>
                        <a href={CONTACT.emailHref} data-testid="footer-email" className="flex items-center gap-3 text-zinc-300 hover:text-blue-400 transition-colors">
                            <span className="h-9 w-9 rounded-sm bg-zinc-900 border border-zinc-800 flex items-center justify-center">
                                <Mail className="h-4 w-4 text-zinc-300" />
                            </span>
                            <span className="text-sm">{CONTACT.email}</span>
                        </a>
                    </div>
                </div>

                <div className="md:col-span-2">
                    <div className="eyebrow mb-4">Explore</div>
                    <ul className="space-y-2 text-sm">
                        <li><Link to="/reviews" className="text-zinc-300 hover:text-white transition-colors" data-testid="footer-link-reviews">Reviews</Link></li>
                        <li><Link to="/compare" className="text-zinc-300 hover:text-white transition-colors" data-testid="footer-link-compare">Compare</Link></li>
                        <li><Link to="/blog" className="text-zinc-300 hover:text-white transition-colors" data-testid="footer-link-blog">Blog</Link></li>
                        <li><Link to="/faq" className="text-zinc-300 hover:text-white transition-colors" data-testid="footer-link-faq">FAQ</Link></li>
                        <li><Link to="/security" className="text-zinc-300 hover:text-white transition-colors" data-testid="footer-link-security">Printer Security</Link></li>
                        <li><Link to="/about" className="text-zinc-300 hover:text-white transition-colors" data-testid="footer-link-about">About</Link></li>
                        <li><Link to="/contact" className="text-zinc-300 hover:text-white transition-colors" data-testid="footer-link-contact">Contact</Link></li>
                    </ul>
                </div>

                <div className="md:col-span-3">
                    <div className="eyebrow mb-4">Categories</div>
                    <ul className="space-y-2 text-sm">
                        <li><Link to="/reviews?type=inkjet" className="text-zinc-300 hover:text-white transition-colors">Inkjet Printers</Link></li>
                        <li><Link to="/reviews?type=laser" className="text-zinc-300 hover:text-white transition-colors">Laser Printers</Link></li>
                        <li><Link to="/reviews?type=all-in-one" className="text-zinc-300 hover:text-white transition-colors">All-in-One</Link></li>
                        <li><Link to="/reviews?type=3d" className="text-zinc-300 hover:text-white transition-colors">3D Printers</Link></li>
                    </ul>
                </div>

                <div className="md:col-span-3">
                    <div className="eyebrow mb-4">Legal</div>
                    <ul className="space-y-2 text-sm">
                        <li><Link to="/privacy" className="text-zinc-300 hover:text-white transition-colors" data-testid="footer-link-privacy">Privacy Policy</Link></li>
                        <li><Link to="/terms" className="text-zinc-300 hover:text-white transition-colors" data-testid="footer-link-terms">Terms of Service</Link></li>
                        <li><Link to="/disclaimer" className="text-zinc-300 hover:text-white transition-colors" data-testid="footer-link-disclaimer">Disclaimer</Link></li>
                        <li><Link to="/privacy#5-affiliate-and-editorial-disclosure" className="text-zinc-300 hover:text-white transition-colors">Advertising Disclosure</Link></li>
                    </ul>
                </div>
            </div>

            {/* Educational + trademark disclaimer strip */}
            <div className="border-t border-zinc-900 bg-zinc-950/60" data-testid="educational-disclaimer">
                <div className="container-tight py-4 text-[11px] text-zinc-400 leading-relaxed">
                    <strong className="text-zinc-200">Educational content:</strong>{" "}
                    All About Printers is an independent publication. Content on this site is provided for
                    informational and educational purposes only and does not constitute professional advice.
                    We are not endorsed by, sponsored by, or officially affiliated with any printer brand
                    unless explicitly stated in a specific piece of content.{" "}
                    <Link to="/disclaimer" className="text-zinc-200 hover:text-white underline underline-offset-2">Read the full disclaimer</Link>.
                </div>
            </div>

            {/* Advertising / affiliate disclosure strip */}
            <div className="border-t border-zinc-900" data-testid="ad-disclosure">
                <div className="container-tight py-4 text-[11px] text-zinc-500 leading-relaxed">
                    <strong className="text-zinc-300">Advertising &amp; Affiliate Disclosure:</strong>{" "}
                    All About Printers is reader-supported. When you buy through links on our site, we may earn an
                    affiliate commission at no additional cost to you. Editorial ratings and rankings are independent
                    of any commercial arrangement. See our{" "}
                    <Link to="/privacy" className="text-zinc-300 hover:text-white underline underline-offset-2">privacy policy</Link>{" "}
                    for details on how we handle your data.
                </div>
            </div>

            {/* Trademark notice */}
            <div className="border-t border-zinc-900" data-testid="trademark-notice">
                <div className="container-tight py-3 text-[10px] text-zinc-500 leading-relaxed">
                    All product names, brand names, trademarks, and registered trademarks are the property of their respective owners.
                    Their use here is for identification and editorial reference only.
                </div>
            </div>

            <div className="border-t border-zinc-900">
                <div className="container-tight py-6 flex flex-col md:flex-row items-center justify-between gap-4">
                    <div className="text-xs text-zinc-500">
                        © {new Date().getFullYear()} All About Printers. Reviews reflect our editorial opinion, not certified test results.
                    </div>
                    <div className="flex items-center gap-3 text-zinc-500">
                        <a href="#" aria-label="Twitter" className="hover:text-white transition-colors"><Twitter className="h-4 w-4" /></a>
                        <a href="#" aria-label="GitHub" className="hover:text-white transition-colors"><Github className="h-4 w-4" /></a>
                        <a href="#" aria-label="RSS" className="hover:text-white transition-colors"><Rss className="h-4 w-4" /></a>
                    </div>
                </div>
            </div>
        </footer>
    );
}
